import { useEffect, useMemo, useRef, useState } from 'react';

import { useContainerMinWidth } from '@backrs_ui/common-ui';

import { formatLongDate } from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  AnsweredQuestion,
  answeredFromLiveQuiz,
  buildResultModel,
  correctIndexOf,
  QuizResultModel,
} from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/quizScoring';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useGetQuizAttemptAnswersQuery,
  useRecordQuizAttemptMutation,
  useSetDayCompletionMutation,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.api';
import {
  recordQuizAnswer,
  selectBoosterQuizProgress,
  setQuizIndex,
  setQuizShowResults,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import {
  DailyQuestion,
  PlanDay,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';
import { revealElement } from '@backrs_ui/grow-ui/src/utils/scrollReveal';

import { Box, CircularProgress, SxProps, Theme, Typography } from '@mui/material';

import { useQuizAgentContext } from './PracticeQuiz.agentContext';
import { QuizFeedbackCard } from './QuizFeedbackCard';
import { QuizResults } from './QuizResults';
import { QuizWizard } from './QuizWizard';
import { BoosterInlineError } from '../BoosterInlineError';
import { SectionHeader } from '../SectionHeader';

const STRINGS = appStrings.modules.satActBooster.dailyPractice.quiz;
const RESULTS_STRINGS = appStrings.modules.satActBooster.dailyPractice.results;
const COMPLETION_ERROR = appStrings.modules.satActBooster.dailyPractice.completionError;

const FEEDBACK_SPLIT_WIDTH = 720;
const SPLIT_AT = `@container (min-width: ${FEEDBACK_SPLIT_WIDTH}px)`;

type PracticeQuizProps = Readonly<{
  day: PlanDay;
  submissionId: string;
  /** The day's fixed five, fetched by the panel; they render in the given order. Empty while fetching. */
  questions: readonly DailyQuestion[];
  isQuestionsFetching: boolean;
  isQuestionsError: boolean;
  onRetryQuestions: () => void;
  /** Jumps to the next day holding a quiz; omitted when this is the last one. */
  onNextQuiz?: () => void;
}>;

// A results-aware Lubav prompt: the full breakdown once results are in, a lighter nudge before then.
const buildDiscussPrompt = (dayDate: string, model: QuizResultModel | null): string => {
  const date = formatLongDate(dayDate);

  if (!model) return RESULTS_STRINGS.discussPromptPending(date);

  const breakdown = model.bySkill
    .map((tally) => `${tally.skill} ${tally.correct}/${tally.total}`)
    .join(', ');

  return RESULTS_STRINGS.discussPrompt(date, model.score, model.total, breakdown);
};

/**
 * Stands in for the quiz on a study day whose topic has no questions yet. Reads as pending rather
 * than failed, so the day's target and resource still read as the work for the day.
 */
const QuizComingSoon = () => (
  <Box sx={comingSoonSx} id="booster-quiz-coming-soon">
    <Typography variant="text-md-semibold" sx={comingSoonTitleSx}>
      {STRINGS.comingSoonTitle}
    </Typography>
    <Typography variant="text-sm-regular" sx={comingSoonBodySx}>
      {STRINGS.comingSoonBody}
    </Typography>
  </Box>
);

/**
 * The study-day practice quiz. A day with no recorded attempt runs the five-question wizard
 * (select → submit, one at a time) whose progress lives in the slice keyed by date, so navigating
 * away mid-quiz returns exactly where you were. A day that already carries an attempt renders its
 * results view reconstructed from the persisted answers — no retake.
 */
export const PracticeQuiz = ({
  day,
  submissionId,
  questions: dailyQuestions,
  isQuestionsFetching,
  isQuestionsError,
  onRetryQuestions,
  onNextQuiz,
}: PracticeQuizProps) => {
  const dispatch = useAppDispatch();
  const scoreCategoryId = day.targets[0]?.scoreCategoryId ?? null;

  const progress = useAppSelector(selectBoosterQuizProgress(day.date));
  // A completed day shows results; the wizard is only reachable while the day has no attempt and
  // this session hasn't started it. In-session progress takes priority so a just-finished quiz
  // keeps its live results without waiting on a refetch.
  const showDurable = day.attempt !== null && progress === undefined;

  const {
    data: persisted,
    isFetching: isAnswersFetching,
    isError: isAnswersError,
    refetch: refetchAnswers,
  } = useGetQuizAttemptAnswersQuery({ submissionId, dayDate: day.date }, { skip: !showDurable });

  const [recordAttempt, { isLoading: isSavingAttempt }] = useRecordQuizAttemptMutation();
  const [setDayCompletion] = useSetDayCompletionMutation();

  const answers = useMemo(() => progress?.answers ?? {}, [progress]);
  const currentIndex = progress?.currentIndex ?? 0;
  const showResults = progress?.showResults ?? false;

  // The tentative pick before Submit locks it in; reset per day by the panel's remount key.
  const [drafts, setDrafts] = useState<Record<number, number>>({});

  // Measured on the always-rendered root, not the quiz row: the row mounts only after questions
  // load, too late for the observer's mount-only effect.
  const [quizRootRef, isSplit] = useContainerMinWidth<HTMLDivElement>(FEEDBACK_SPLIT_WIDTH);
  // Set by the Submit click alone: `submitted` is restored from the slice, so keying the reveal on
  // it would also fire on arrival and on Back to an answered question.
  const revealOnSubmitRef = useRef(false);

  const total = dailyQuestions.length;
  const currentQuestion = dailyQuestions[currentIndex];
  const submitted = answers[currentIndex] !== undefined;
  const selectedOptionIndex = submitted ? answers[currentIndex] : (drafts[currentIndex] ?? null);

  const correctOptionIndex = useMemo(
    () => (currentQuestion ? correctIndexOf(currentQuestion) : -1),
    [currentQuestion],
  );

  // Split widths pin the feedback in view on their own, so only the stacked case needs to move.
  useEffect(() => {
    if (!submitted || !revealOnSubmitRef.current) return;

    revealOnSubmitRef.current = false;

    if (!isSplit) revealElement(document.getElementById('booster-quiz-feedback'));
  }, [submitted, isSplit]);

  const liveModel = useMemo(
    () => buildResultModel(answeredFromLiveQuiz(dailyQuestions, answers)),
    [dailyQuestions, answers],
  );

  // Each answer embeds its question, so a completed day's results reconstruct from this alone.
  const durableAnswered = useMemo((): AnsweredQuestion[] => {
    if (!showDurable || !persisted) return [];

    return persisted.answers
      .map((answer): AnsweredQuestion | null =>
        answer.question
          ? {
              question: answer.question,
              chosenIndex: answer.chosenOptionIndex,
              correct: answer.isCorrect,
            }
          : null,
      )
      .filter((entry): entry is AnsweredQuestion => entry !== null);
  }, [showDurable, persisted]);

  const durableModel = useMemo(
    (): QuizResultModel | null =>
      showDurable && persisted
        ? buildResultModel(durableAnswered, { score: persisted.score, total: persisted.total })
        : null,
    [showDurable, persisted, durableAnswered],
  );

  // Results are revealed only once the attempt is stored, so a student never reads a discarded
  // attempt as saved. A failed save leaves the wizard on its last question, so Next retries it.
  const finishQuiz = async () => {
    if (scoreCategoryId == null) return;

    // Send the chosen option's stable id (null when blank); the server grades and scores.
    const attemptAnswers = dailyQuestions.map((question, index) => {
      const chosenIndex = answers[index];

      return {
        questionOrder: index,
        questionId: question.id,
        chosenOptionId:
          chosenIndex === undefined ? null : (question.options[chosenIndex]?.id ?? null),
      };
    });

    try {
      await recordAttempt({
        submissionId,
        dayDate: day.date,
        scoreCategoryId,
        // score/total are client-only here — they drive the optimistic day overlay, not the write.
        score: liveModel.score,
        total: liveModel.total,
        answers: attemptAnswers,
      }).unwrap();
    } catch {
      dispatch(showSnackbar({ message: STRINGS.saveError, severity: 'error' }));

      return;
    }

    dispatch(setQuizShowResults({ dayDate: day.date, showResults: true }));

    // Finishing the quiz auto-completes the day (only if it isn't already); the pill stays
    // manually togglable afterward. The attempt is already stored, so a failure here only
    // costs the completion pill.
    if (!day.completed) {
      try {
        await setDayCompletion({ submissionId, dayDate: day.date, completed: true }).unwrap();
      } catch {
        dispatch(showSnackbar({ message: COMPLETION_ERROR, severity: 'error' }));
      }
    }
  };

  const activeModel = showDurable ? durableModel : showResults ? liveModel : null;

  const answeredForContext = useMemo(
    () => (showDurable ? durableAnswered : answeredFromLiveQuiz(dailyQuestions, answers)),
    [showDurable, durableAnswered, dailyQuestions, answers],
  );

  useQuizAgentContext({
    dayDate: day.date,
    answered: answeredForContext,
    completed: showDurable || showResults,
    score: activeModel?.score ?? liveModel.score,
    total: activeModel?.total ?? total,
  });

  const content = () => {
    if (scoreCategoryId == null) return <QuizComingSoon />;

    if (isQuestionsFetching || (showDurable && isAnswersFetching)) {
      return (
        <Box sx={loaderSx}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    // A failed fetch must not fall through to the coming-soon copy or the score-only results
    // card — both read as a legitimate answer about the day. The durable view is built from the
    // persisted answers alone, so this query failing there costs it nothing.
    if (isQuestionsError && !showDurable) {
      return (
        <BoosterInlineError
          id="booster-quiz-questions-error"
          message={STRINGS.questionsError}
          onRetry={onRetryQuestions}
        />
      );
    }

    if (showDurable && isAnswersError) {
      return (
        <BoosterInlineError
          id="booster-quiz-answers-error"
          message={STRINGS.answersError}
          onRetry={refetchAnswers}
        />
      );
    }

    if (showDurable) {
      // A day whose attempt has no answer rows (recorded before answers were persisted) falls back
      // to the recorded score alone rather than crashing.
      const model =
        durableModel ??
        buildResultModel([], { score: day.attempt?.score ?? 0, total: day.attempt?.total ?? 0 });

      return <QuizResults model={model} onNextQuiz={onNextQuiz} />;
    }

    if (total === 0) return <QuizComingSoon />;

    if (showResults) return <QuizResults model={liveModel} onNextQuiz={onNextQuiz} />;

    if (!currentQuestion) return null;

    const feedback = submitted ? (
      <QuizFeedbackCard
        correct={selectedOptionIndex === correctOptionIndex}
        options={currentQuestion.options}
        explanation={currentQuestion.explanation}
      />
    ) : null;

    return (
      <Box sx={quizRowSx}>
        <Box sx={quizColSx}>
          <QuizWizard
            question={currentQuestion}
            index={currentIndex}
            total={total}
            selectedOptionIndex={selectedOptionIndex}
            submitted={submitted}
            feedback={isSplit ? undefined : feedback}
            onSelect={(optionIndex) => {
              if (submitted) return;

              setDrafts((prev) => ({ ...prev, [currentIndex]: optionIndex }));
            }}
            onSubmit={() => {
              if (drafts[currentIndex] === undefined) return;

              revealOnSubmitRef.current = true;

              dispatch(
                recordQuizAnswer({
                  dayDate: day.date,
                  questionIndex: currentIndex,
                  optionIndex: drafts[currentIndex],
                }),
              );
            }}
            onBack={() =>
              dispatch(setQuizIndex({ dayDate: day.date, index: Math.max(0, currentIndex - 1) }))
            }
            onNext={() => {
              if (currentIndex >= total - 1) void finishQuiz();
              else dispatch(setQuizIndex({ dayDate: day.date, index: currentIndex + 1 }));
            }}
            canBack={currentIndex > 0}
            isLast={currentIndex === total - 1}
            nextDisabled={isSavingAttempt}
          />
        </Box>
        {/* Reserves its width from the start so the quiz card never resizes on submit. */}
        {isSplit && (
          <Box sx={feedbackColSx}>
            <Box sx={feedbackStickySx}>{feedback}</Box>
          </Box>
        )}
      </Box>
    );
  };

  const showDiscuss = scoreCategoryId != null && (showDurable || total > 0);

  return (
    <Box sx={rootSx} id="booster-practice-quiz" ref={quizRootRef}>
      <SectionHeader
        title={STRINGS.title}
        action={
          showDiscuss ? (
            <LubavButton
              id="booster-quiz-discuss-btn"
              label={RESULTS_STRINGS.discussLabel}
              chatMessage={RESULTS_STRINGS.discussChatMessage}
              prompt={buildDiscussPrompt(day.date, activeModel)}
              disabled={activeModel === null}
            />
          ) : undefined
        }
      />
      {content()}
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

// Flips to two columns on the panel's own width (see the panel's size container), not the viewport.
const quizRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  [SPLIT_AT]: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
});

const quizColSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  [SPLIT_AT]: { flex: '1 1 0' },
};

// Fixed ~1/3 column beside the quiz, full height so the card inside has room to stick.
const feedbackColSx: SxProps<Theme> = {
  width: '100%',
  [SPLIT_AT]: { flex: '0 1 320px', maxWidth: '360px' },
};

// The quiz card runs past the fold, so the feedback follows the student down it.
const feedbackStickySx: SxProps<Theme> = ({ backrs }) => ({
  position: 'sticky',
  top: backrs.spacing['spacing-xl'],
});

const loaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-4xl'],
});

const comingSoonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  padding: backrs.spacing['spacing-3xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const comingSoonTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const comingSoonBodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
